import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';

const TITLES = {
  objectives: 'Obiettivi del post',
  targets: 'Target / Audience',
  structures: 'Strutture testo',
  hooks: 'Tipi di Hook',
  ctas: 'Tipi di CTA',
  'event-types': 'Tipi di evento',
  'scout-search-types': 'Tipi di ricerca Scout',
};

export default function ConfigDialog({ open, onClose, configType, onUpdate }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });
  const [newForm, setNewForm] = useState({ label: '', description: '' });
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!configType) return;
    setLoading(true);
    try {
      const res = await api.get(`/content-config/${configType}`);
      setItems(res.data || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [configType]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleAdd = async () => {
    if (!newForm.label.trim()) return;
    try {
      await api.post(`/content-config/${configType}`, newForm);
      setNewForm({ label: '', description: '' });
      setAdding(false);
      load();
      onUpdate?.();
      toast.success('Aggiunto');
    } catch { toast.error('Errore'); }
  };

  const handleUpdate = async (id) => {
    try {
      await api.patch(`/content-config/${configType}/${id}`, editForm);
      setEditingId(null);
      load();
      onUpdate?.();
      toast.success('Aggiornato');
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/content-config/${configType}/${id}`);
      load();
      onUpdate?.();
      toast.success('Eliminato');
    } catch { toast.error('Errore'); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">{TITLES[configType] || configType}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 py-2" data-testid="config-dialog-list">
          {loading && <Loader2 className="w-4 h-4 animate-spin mx-auto" />}
          {items.map(item => (
            <div key={item.id} className="rounded-lg border p-2.5 group" style={{ borderColor: '#E5E7EB' }}>
              {editingId === item.id ? (
                <div className="space-y-2">
                  <Input value={editForm.label} onChange={e => setEditForm(p => ({ ...p, label: e.target.value }))}
                    placeholder="Nome" className="text-xs h-8" data-testid={`edit-label-${item.id}`} />
                  <Textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Descrizione (opzionale)" rows={2} className="text-xs" />
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-7 px-3 text-xs" onClick={() => handleUpdate(item.id)}
                      data-testid={`save-edit-${item.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Salva
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingId(null)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>{item.label || item.name}</p>
                    {(item.description || item.example) && (
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-muted)' }}>{item.description || item.example}</p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setEditingId(item.id); setEditForm({ label: item.label || item.name || '', description: item.description || '' }); }}
                      className="p-1 rounded hover:bg-gray-100" data-testid={`edit-btn-${item.id}`}>
                      <Pencil className="w-3 h-3 text-muted-foreground" />
                    </button>
                    <button onClick={() => handleDelete(item.id)}
                      className="p-1 rounded hover:bg-red-50" data-testid={`delete-btn-${item.id}`}>
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add new */}
        {adding ? (
          <div className="border-t pt-3 space-y-2">
            <Input value={newForm.label} onChange={e => setNewForm(p => ({ ...p, label: e.target.value }))}
              placeholder="Nome" className="text-xs h-8" data-testid="new-item-label" />
            <Textarea value={newForm.description} onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Descrizione (opzionale)" rows={2} className="text-xs" data-testid="new-item-desc" />
            <div className="flex gap-1.5">
              <Button size="sm" className="h-7 px-3 text-xs" onClick={handleAdd} data-testid="add-item-confirm">
                <Check className="w-3 h-3 mr-1" /> Aggiungi
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setAdding(false); setNewForm({ label: '', description: '' }); }}>
                Annulla
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setAdding(true)} data-testid="add-new-item">
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi nuovo
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
